import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import useCenterlineAiStyles from './styles';
import centerlineLogo from '../../images/centerline-logo-rounded.png';
import { activeChat, showsWelcome } from './core/chat-reducer';
import { CitationInfo } from './core/types';
import { useCenterlineAi } from './CenterlineAiProvider';
import { TurnView } from './Messages';
import CitationCard, { CitationCardContext, OpenCard } from './CitationCard';

/** The active chat's messages (chat-ui-spec §1.3, §1.5, §5.10). */

/** How close to the bottom counts as "at the bottom" for following new text. */
const STICK_PX = 80;

export function WelcomeState() {
  const classes = useCenterlineAiStyles();
  return (
    <div className={classes.welcome}>
      <img className={classes.welcomeLogo} src={centerlineLogo} alt="" width={48} height={48} />
      <h2 className={classes.welcomeTitle}>Have a conversation with your project</h2>
    </div>
  );
}

export default function Thread() {
  const classes = useCenterlineAiStyles();
  const { state } = useCenterlineAi();
  const chat = activeChat(state);
  const welcome = showsWelcome(state);
  const scroller = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const shownId = useRef<number | null>(null);
  const turnCount = useRef(0);
  const [card, setCard] = useState<OpenCard | null>(null);
  // Where the thread was scrolled when the card opened: a scroll away from there closes it.
  const cardScrollTop = useRef(0);

  const closeCard = useCallback(() => setCard(null), []);
  const cardControls = useMemo(
    () => ({
      openFor: card?.anchor ?? null,
      toggle: (anchor: HTMLElement, n: number, info: Partial<CitationInfo>) => {
        cardScrollTop.current = scroller.current?.scrollTop ?? 0;
        setCard((current) => (current?.anchor === anchor ? null : { anchor, n, info }));
      },
    }),
    [card],
  );

  // A card belongs to the chat it was opened in.
  const chatId = chat?.localId;
  useEffect(() => setCard(null), [chatId]);

  // Follow new text only while the reader is at the bottom; a newly shown chat or a new question
  // always goes to the bottom.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el || !chat) return;
    if (shownId.current !== chat.localId) {
      shownId.current = chat.localId;
      stick.current = true;
    } else if (chat.turns.length > turnCount.current) {
      stick.current = true;
    }
    turnCount.current = chat.turns.length;
    if (stick.current) el.scrollTop = el.scrollHeight;
  }, [chat]);

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < STICK_PX;
    if (card && Math.abs(el.scrollTop - cardScrollTop.current) > 2) setCard(null);
  };

  if (!chat) return null;
  return (
    <CitationCardContext.Provider value={cardControls}>
      <div
        className={clsx(classes.messages, welcome && classes.messagesWelcome)}
        ref={scroller}
        onScroll={onScroll}
      >
        <div className={classes.thread}>
          {welcome ? <WelcomeState /> : null}
          {chat.turns.map((turn) => (
            <TurnView key={turn.key} chatId={chat.localId} turn={turn} />
          ))}
        </div>
      </div>
      <CitationCard card={card} onClose={closeCard} />
    </CitationCardContext.Provider>
  );
}
